import Button from 'components/button';
import { useCrmLocale } from 'components/crm/ui/crmTime';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import ListIcon from 'components/record/ListIcon';
import { useLinkLabel } from 'components/record/deletedGroup';
import CellButton from 'components/table/components/CellButton';
import EmptyCell from 'components/table/components/EmptyCell';
import ScrollableTableWrapper from 'components/table/components/ScrollableTableWrapper';
import TableCell from 'components/table/components/TableCell';
import TableRow from 'components/table/components/TableRow';
import TableSkeleton from 'components/table/components/TableSkeleton';
import Thead from 'components/table/components/thead';
import { apiErrorText, isCanceledRequest } from 'helper/apiErrorText';
import { recordOfLink, rowLinks } from 'helper/taskLinks';
import useLeavingItems, { ROW_LEAVE_CLASS } from 'hooks/useLeavingItems';
import useText from 'hooks/useText';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import LINK_ICONS from './linkIcons';
import { ROW_CASCADE_CLASS, rowCascadeDelay } from 'utils/surfaceClasses';

const formatDeletedAt = (iso, locale) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(date);
};

const matchesQuery = (task, query) => {
  const words = query.trim().toLowerCase();
  if (!words) return true;
  const labels = rowLinks(task).map((link) => link.label ?? '');
  return [task.title ?? '', ...labels].join(' ').toLowerCase().includes(words);
};

/**
 * The « Supprimées » tab of « Mes tâches »: the tasks deleted lately (the
 * api's last 50, an agency manager's all, the others' their own), each with
 * its record, when and by whom it was deleted, and a button that brings it
 * back. Mounted when the tab opens, it reads the list then and only then.
 * `call` is useCrmContext's; `onRestored` runs once a task is back.
 */
const DeletedTaskTable = ({ call, query = '', onOpenCompany, onRestored }) => {
  const { text, language } = useText('tasks');
  const locale = useCrmLocale();
  const linkLabel = useLinkLabel();
  const [tasks, setTasks] = useState(null);
  const [failed, setFailed] = useState(false);
  const [restoring, setRestoring] = useState({});
  const { leave, isLeaving, onLeaveEnd } = useLeavingItems();
  const mounted = useRef(true);

  const load = useCallback(() => {
    setFailed(false);
    setTasks(null);
    return call(crmApi.listDeletedTasks).then(
      (list) => mounted.current && setTasks(list),
      (error) => {
        if (mounted.current && !isCanceledRequest(error)) setFailed(true);
      },
    );
  }, [call]);
  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  const restore = async (task) => {
    const settle = () => mounted.current && setRestoring(({ [task.id]: _done, ...rest }) => rest);
    setRestoring((current) => ({ ...current, [task.id]: true }));
    try {
      await call(crmApi.restoreTask, { id: task.id });
    } catch (error) {
      settle();
      const message = apiErrorText(error, { language, text, group: 'errors' });
      if (message) toast.error(message);
      return;
    }
    settle();
    leave(task.id, () => setTasks((current) => current.filter((one) => one.id !== task.id)));
    toast.success(text('restored'));
    onRestored?.();
  };

  if (failed) return <CompactEmptyState title={text('loadError')} action={{ label: text('retry'), onClick: load }} />;
  const shown = (tasks ?? []).filter((task) => matchesQuery(task, query));
  if (tasks && !shown.length) {
    return query.trim() ? (
      <CompactEmptyState glyph="searchX" title={text('emptyFilteredTitle')} description={text('emptyFilteredText')} />
    ) : (
      <CompactEmptyState glyph="inbox" title={text('emptyDeletedTitle')} description={text('emptyDeletedText')} />
    );
  }

  const header = [text('columnTask'), text('columnLinked'), text('columnDeletedAt'), text('columnDeletedBy')];
  // Thead adds an empty column for the actions.
  const columns = header.length + 1;
  return (
    <ScrollableTableWrapper>
      <table className="w-full table-auto border-separate border-spacing-0 text-gray-500 ft-sm [&>tbody>tr>td]:border-b [&>tbody>tr>td]:border-gray-100 [&>thead>tr>th]:border-gray-100">
        <thead className="sticky top-0 z-10">
          <Thead champsHeader={header} pathname="/tasks" />
        </thead>
        {!tasks && <TableSkeleton columns={columns} rows={6} label={text('loadingDeleted')} />}
        {tasks && (
          <tbody>
            {shown.map((task, index) => {
              const first = rowLinks(task)[0];
              const target = first ? recordOfLink(first) : null;
              const FirstIcon = first ? LINK_ICONS[first.type] : null;
              const leaving = isLeaving(task.id);
              return (
                <TableRow
                  key={task.id}
                  itemId={task.id}
                  clickable={false}
                  className={`h-14 ${leaving ? ROW_LEAVE_CLASS : ROW_CASCADE_CLASS}`}
                  style={leaving ? undefined : rowCascadeDelay(index)}
                  onAnimationEnd={onLeaveEnd(task.id)}
                >
                  <TableCell compact position="first">
                    <span className="block truncate font-medium text-gray-900">{task.title}</span>
                  </TableCell>
                  <TableCell compact>
                    {!first && <EmptyCell />}
                    {first && target && (
                      <CellButton icon={FirstIcon} tooltip={text('openCompany')} onClick={() => onOpenCompany(target)}>
                        {first.label}
                      </CellButton>
                    )}
                    {first && !target && (
                      <span className="-ml-1.5 flex min-w-0 items-center gap-3 px-1.5 py-1 text-gray-700">
                        <ListIcon icon={FirstIcon} size="xs" />
                        <span className="truncate">{linkLabel(first)}</span>
                      </span>
                    )}
                  </TableCell>
                  <TableCell compact>
                    <span className="whitespace-nowrap text-gray-700">{formatDeletedAt(task.deletedAt, locale)}</span>
                  </TableCell>
                  <TableCell compact>
                    {task.deletedBy?.name ? (
                      <span className="truncate text-gray-700">{task.deletedBy.name}</span>
                    ) : (
                      <EmptyCell />
                    )}
                  </TableCell>
                  <TableCell compact position="last">
                    <span className="flex justify-end">
                      <Button
                        color="SECONDARY"
                        size="SM"
                        className="shrink-0"
                        disabled={Boolean(restoring[task.id]) || leaving}
                        onClick={() => restore(task)}
                      >
                        {text('restore')}
                      </Button>
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        )}
      </table>
    </ScrollableTableWrapper>
  );
};

export default DeletedTaskTable;
